import type { BootstrapResult } from '../lib/api/client'

type Tone = 'info' | 'ok' | 'danger'

const TONE: Record<Tone, { fg: string; bg: string; border: string }> = {
  info: { fg: 'var(--accent)', bg: 'var(--accent-tint)', border: 'var(--accent-tint-2)' },
  ok: { fg: 'var(--success-fg)', bg: 'var(--success-bg)', border: 'var(--success-bg)' },
  danger: { fg: 'var(--danger-fg)', bg: 'var(--danger-bg)', border: 'var(--danger-bg)' },
}

function Banner({ tone, children }: { tone: Tone; children: React.ReactNode }) {
  const c = TONE[tone]
  return (
    <div
      role="status"
      style={{
        padding: '10px 14px',
        marginBottom: 12,
        borderRadius: 8,
        fontSize: 13,
        color: c.fg,
        background: c.bg,
        border: `1px solid ${c.border}`,
      }}
    >
      {children}
    </div>
  )
}

/**
 * Surfaces the state of a B24 bootstrap (sync) mutation: progress while it runs,
 * a result summary on success, and the error on failure. Without this the sync
 * button failed silently — the run threw, the spinner stopped, and nothing told
 * the user what happened.
 */
export function SyncStatus({
  isPending,
  isError,
  error,
  data,
}: {
  isPending: boolean
  isError: boolean
  error: unknown
  data?: BootstrapResult
}) {
  if (isPending) {
    return <Banner tone="info">Синхронізація з Bitrix24… (може зайняти трохи часу)</Banner>
  }
  if (isError) {
    const msg = error instanceof Error ? error.message : 'невідома помилка'
    return <Banner tone="danger">Помилка синхронізації: {msg}</Banner>
  }
  if (data) {
    const rec = data.reconcile
    const errorCount = data.errorCount + (rec?.errorCount ?? 0)
    const samples = [...data.errorSamples, ...(rec?.errorSamples ?? [])].slice(0, 5)
    const shownErrors = errorCount > samples.length
    return (
      <Banner tone={errorCount > 0 ? 'danger' : 'ok'}>
        <div>
          Синхронізовано: +{data.inserted} нових · {data.updated} оновлено · {data.skipped} без змін
          {data.skippedClosed > 0 ? ` · ${data.skippedClosed} поза областю` : ''}
          {data.errorCount > 0 ? ` · ${data.errorCount} помилок` : ''}
        </div>
        {rec && (
          <div style={{ marginTop: 2 }}>
            Звірка оплат: {rec.settled} оплат проведено
            {rec.updated > 0 ? ` · ${rec.updated} оновлено` : ''}
            {rec.errorCount > 0 ? ` · ${rec.errorCount} помилок` : ''}
          </div>
        )}
        {samples.length > 0 && (
          <div style={{ marginTop: 4, opacity: 0.85, fontSize: 12 }}>
            {samples.join('; ')}
            {shownErrors ? ' …' : ''}
          </div>
        )}
      </Banner>
    )
  }
  return null
}
